import { API_ROUTES } from "@/constants/api";
import { QUERY_KEYS } from "@/constants/config";
import { ANALYTICS_DEFAULTS } from "@/constants/workspaces";
import { apiGet } from "@/lib/api-client";
import { useQuery } from "@tanstack/react-query";
import type { OrdersSeriesPoint, RevenueSummary, SeriesBucket, TopProductRow } from "@bizy/core";

export interface RangeParams {
  days?: number;
  bucket?: SeriesBucket;
  limit?: number;
}

const toQuery = (params: RangeParams): string => {
  const search = new URLSearchParams();
  if (params.days) search.set("from", new Date(Date.now() - params.days * 86_400_000).toISOString());
  if (params.bucket) search.set("bucket", params.bucket);
  if (params.limit) search.set("limit", String(params.limit));
  const query = search.toString();
  return query.length > 0 ? `?${query}` : "";
};

export type RevenueSummaryPayload = RevenueSummary & { from: string; to: string };

export const useRevenueSummary = (workspaceId: string | undefined, days = ANALYTICS_DEFAULTS.rangeDays) =>
  useQuery({
    queryKey: [QUERY_KEYS.analytics, workspaceId ?? "none", "summary", days],
    enabled: Boolean(workspaceId),
    queryFn: () =>
      apiGet<RevenueSummaryPayload>(
        `${API_ROUTES.analyticsSummary(workspaceId as string)}${toQuery({ days })}`,
      ),
  });

export const useOrdersSeries = (
  workspaceId: string | undefined,
  bucket: SeriesBucket = "day",
  days = ANALYTICS_DEFAULTS.rangeDays,
) =>
  useQuery({
    queryKey: [QUERY_KEYS.analytics, workspaceId ?? "none", "series", bucket, days],
    enabled: Boolean(workspaceId),
    queryFn: () =>
      apiGet<{ points: OrdersSeriesPoint[]; bucket: string; from: string; to: string }>(
        `${API_ROUTES.analyticsSeries(workspaceId as string)}${toQuery({ days, bucket })}`,
      ),
  });

export const useTopProducts = (workspaceId: string | undefined, days = ANALYTICS_DEFAULTS.rangeDays) =>
  useQuery({
    queryKey: [QUERY_KEYS.analytics, workspaceId ?? "none", "top", days],
    enabled: Boolean(workspaceId),
    queryFn: () =>
      apiGet<{ items: TopProductRow[]; from: string; to: string }>(
        `${API_ROUTES.analyticsTopProducts(workspaceId as string)}${toQuery({ days, limit: ANALYTICS_DEFAULTS.topProductLimit })}`,
      ),
  });
